// src/auth/canonicalRecovery.ts
//
// Single-flight canonical re-authentication.
//
// The live failure was not only "no redirect" — it was a STORM. Once the RP
// session expired, `/auth/access-version` and `/api/contacts` kept returning
// 401 on every poll tick, indefinitely. Any per-request redirect would have
// turned that storm into a navigation storm instead.
//
// So recovery is latched: the FIRST request that proves the session is gone
// starts exactly one navigation, and every subsequent 401 is absorbed. The
// latch is module-scoped because the navigation it guards is module-scoped —
// there is one browser, and it can only go to one place.
//
// The latch is never reset in-page. A successful navigation replaces the
// document, which resets it naturally; if navigation is somehow suppressed,
// staying latched is the safe failure — a quiet page beats a redirect loop.
// `__resetCanonicalRecovery` exists only so tests start from a known state.

import { currentReturnTo } from './currentReturnTo';

let recoveryStarted = false;

/** Test seam. Never call from application code. */
export function __resetCanonicalRecovery(): void {
  recoveryStarted = false;
}

/** Whether a canonical recovery navigation has already been started. */
export function hasCanonicalRecoveryStarted(): boolean {
  return recoveryStarted;
}

/** CRM's own RP entry point, carrying a safe local return location. */
export function canonicalSignInUrl(): string {
  return `/auth/start?returnTo=${encodeURIComponent(currentReturnTo())}`;
}

/**
 * Begin canonical re-authentication, at most once.
 *
 * Returns true when THIS call started the recovery, false when it was absorbed
 * because recovery was already in flight. Callers use the return value to stop
 * doing further work, not to decide whether to navigate again.
 */
export function beginCanonicalRecovery(
  navigate: (url: string) => void = (url) => {
    if (typeof window !== 'undefined') window.location.assign(url);
  },
): boolean {
  if (recoveryStarted) return false;
  recoveryStarted = true;
  navigate(canonicalSignInUrl());
  return true;
}
